import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ViewChild,
  NgZone,
} from '@angular/core'
import { DomSanitizer, Title } from '@angular/platform-browser'
import { MobilityComponent } from '../mobility/mobility.component'
import { AuthenticationService } from '../../services/index'
import { ContextService } from '../../services/context.service'
import { MobilityService, TERRUNIT_TYPE } from '../../services/mobility.service'
import { IsolinesService } from '../../services/isolines.service'
import { ICartoLayerConfig } from '../../common/map/map.component'
import { bbox } from 'app/utils/geo'
import { TranslateService } from '@ngx-translate/core'
import { HttpClient } from '@angular/common/http'
import carto from '@carto/carto-vl'
import { Colors } from '../../common/config/constants'
import { firstValueFrom, merge, of } from 'rxjs'
import { debounceTime, switchMap, first } from 'rxjs/operators'

@Component({
  standalone: false,
  selector: 'app-retail',
  templateUrl: './retail.component.html',
  styleUrls: ['../mobility/mobility.component.scss', './retail.component.scss'],
})
export class RetailComponent extends MobilityComponent implements OnInit {
  protected _brand = ''
  get brand() {
    return this._brand || this.context.organization.id
  }

  beforeLayoutGeneral = ''
  showStats = false
  showCompareFilterSelector = true
  filterPlaceHolder = 'Search for store in ...'
  competitorSelectorList: any[] = []
  allPois = []
  filteredPoiList: any[] = []
  highlightDst: boolean | number = false
  selectedPOICentroid
  @ViewChild('isochronewidget', { static: false }) isochroneConfigWidget: any
  isochroneLayerId = 'isochroneLayerLayer'
  isIsochroneActive = false
  isochroneStats = {
    area: null,
    poisTotal: null,
    poisCompetitors: null,
  }
  isochroneOpts = [
    {
      value: 'car',
      label: 'by car',
      selected: true,
      options: [
        {
          value: 5,
          label: '5',
          unit: 'min',
          selected: true,
        },
        {
          value: 10,
          label: '10',
          unit: 'min',
          selected: false,
        },
        {
          value: 15,
          label: '15',
          unit: 'min',
          selected: false,
        },
        {
          value: 20,
          label: '20',
          unit: 'min',
          selected: false,
        },
      ],
    },
    {
      value: 'walk',
      label: 'by foot',
      selected: false,
      options: [
        {
          value: 15,
          label: '15',
          unit: 'min',
          selected: true,
        },
        {
          value: 30,
          label: '30',
          unit: 'min',
          selected: false,
        },
        {
          value: 60,
          label: '60',
          unit: 'min',
          selected: false,
        },
        {
          value: 90,
          label: '90',
          unit: 'min',
          selected: false,
        },
      ],
    },
  ]

  constructor(
    protected authService: AuthenticationService,
    protected context: ContextService,
    protected sanitizer: DomSanitizer,
    protected changes: ChangeDetectorRef,
    protected mobilityService: MobilityService,
    protected isolinesService: IsolinesService,
    protected translateService: TranslateService,
    protected titleService: Title,
    protected http: HttpClient,
    protected zone: NgZone,
  ) {
    super(
      authService,
      context,
      sanitizer,
      changes,
      mobilityService,
      translateService,
      titleService,
      zone,
    )
  }

  ngOnInit() {
    this.isLoading = true
    this.mapLoaded = false
    if (!this.currentBasemap) {
      this.basemapList.forEach((elem) => {
        elem.active = false
      })
      this.currentBasemap = this.basemapList[0]
      this.currentBasemap.active = true
    }
    this.setupBrandIcon()
    this.subscriptions.add(
      this.changeEventComponentState
        .asObservable()
        .pipe(
          debounceTime(100),
          switchMap(() => {
            this.isLoading = true
            this._resetComponent()
            this.changes.markForCheck()

            const promise = Promise.all([
              this._processMap(),
              this.context.isoline ? Promise.resolve() : this._processPoi(),
              this.context.isoline ? Promise.resolve() : this._processStats(),
            ]).then(() => {
              this.updateFilteredPois()
              if (this.context.isoline) {
                this.isIsochroneActive = !this.isIsochroneActive
                const time = this.context.isoline.range / 60
                const mode = this.context.isoline.mode
                this.onIsochroneConfigChange({ time, mode })
                this.isochroneOpts = this.isochroneOpts.map((op) => {
                  op.selected = op.value === mode
                  op.options = op.options.map((opt) => {
                    opt.selected = opt.value === time
                    return opt
                  })
                  return op
                })
              } else {
                this.isLoading = false
              }
              this.changes.markForCheck()
            })
            return of(promise)
          }),
        )
        .subscribe(),
    )

    // none of these subscriptions are called on the component initialization phase
    // just when something in the filter bar changes or when you click the map and select something
    // the one subcription that is called on initalization is the one from onMapLoaded
    this.subscriptions.add(
      merge(
        this.context.filterChanges,
        this.context.poiChanges,
        this.context.deliveriesChanges,
        this.context.densityMode,
      ).subscribe(() => {
        if (this.mapLoaded) {
          this.doComponentChanges()
        } else {
          this.changes.markForCheck()
        }
      }),
    )

    this.generalLayerProperties['category_id'] = { type: 'category' }
    this.generalLayerProperties['category_name'] = { type: 'category' }
    this.generalLayerProperties['competitor'] = { type: 'number' }
  }

  setupBrandIcon() {
    this.subscriptions.add(
      this.context.$currentVertical
        .pipe(first((vertical: any) => !!vertical.id))
        .subscribe(() => {
          let iconUrl = `/assets/img/brands/${this.brand}/logo.svg`
          firstValueFrom(this.http.get(iconUrl, { responseType: 'text' }))
            .then((res) => {
              if (res && !res.includes('<svg')) {
                iconUrl = iconUrl.replace(this.brand, 'vodafone')
                this._brand = 'vodafone'
              }
              this.brandIcon = this.sanitizer.bypassSecurityTrustStyle(
                `--opt-brand-icon: url("${iconUrl}")`,
              )
            })
            .catch((res) => {
              if (res.status === 404) {
                iconUrl = iconUrl.replace(this.brand, 'vodafone')
                this._brand = 'vodafone'
              }
              this.brandIcon = this.sanitizer.bypassSecurityTrustStyle(
                `--opt-brand-icon: url("${iconUrl}")`,
              )
            })
        }),
    )
  }

  get organizationName() {
    return this.context.organization.name
  }

  getFilterPlaceHolder() {
    const placeholder = this.translateService.instant('Search for a store in')
    return `${placeholder} ${this.context.city.name}`
  }

  cleanCartoLayers() {
    this.map.removeCartoLayer(this.isochroneLayerId)
    super.cleanCartoLayers()
  }

  canShowTwinAreas() {
    const vertical = this.context.vertical
    if (!vertical) {
      return false
    }
    const active = vertical.delivery.sections.twin_areas
    return active && vertical.delivery.type === 'twinAreas'
  }

  _resetPoi() {
    if (this.selectedPOI === null) {
      this.currentSelectValue = ''
      this.currentCompareValue = ''
    } else {
      const foundPoi = this.poiList.find((p) => p.value === this.selectedPOI)
      this.currentSelectValue = foundPoi && foundPoi.label
    }
  }

  _processPoi(): Promise<any> {
    return this.mobilityService.getTerritoryList().then((data) => {
      this.allPois = data
      this.competitorSelectorList.map((c) => {
        c.count = 0
        return c
      })
      for (const d of data) {
        if (d.competitor) {
          let competitor = this.competitorSelectorList.find(
            (c) => c.id === d.category_id,
          )
          if (!competitor) {
            competitor = {
              id: d.category_id,
              brand: d.category_name,
              name: d.category_name,
              count: 0,
              visible: false,
            }
            this.competitorSelectorList.push(competitor)
          }
          competitor.count++
        }
        // For force reload competitors component
        this.competitorSelectorList = this.competitorSelectorList.slice(0)
        this._processPoiList()
      }
    })
  }

  _processPoiList() {
    this.poiList = []
    for (const d of this.allPois) {
      let insert = false
      if (d.competitor) {
        const competitor = this.competitorSelectorList.find(
          (c) => c.id === d.category_id,
        )
        if (competitor && competitor.visible) {
          insert = true
        }
      } else {
        insert = true
      }
      if (insert) {
        this.poiList.push({
          label: d.name,
          value: d.id,
          region_id: d.region_id.toString(),
          brand: d.category_name,
          centroid: [d.lng, d.lat],
        })
      }
    }
  }

  _processGridStatsByPoi() {
    const poi = this.selectedPOI || null
    return Promise.all([
      this.mobilityService.getTargetStats(poi, null, null, this.compareOptions), // targetStats
      this.mobilityService.getVisitorsAgg(poi, this.compareOptions), // totalVisitors
      this.mobilityService.getRanking(
        TERRUNIT_TYPE.MUNICIPALITIES,
        poi,
        this.compareOptions,
      ), // tripsLocal
      this.mobilityService.getRanking(
        TERRUNIT_TYPE.PROVINCES,
        poi,
        this.compareOptions,
      ), // tripsNational
      this.mobilityService.getRanking(
        TERRUNIT_TYPE.COUNTRIES,
        poi,
        this.compareOptions,
      ), // tripsInternational
      this.mobilityService.getTotalVisitors(poi, this.compareOptions), // tripsSummary
      this.mobilityService.getPoiStats(poi, this.compareOptions), // poiStats
      this.mobilityService.getRanking(
        TERRUNIT_TYPE.BOROUGH,
        poi,
        this.compareOptions,
      ), // tripsBorough
    ])
      .then((values) => {
        this.mobilityStats = {
          targetStats: values[0],
          visitors: values[1],
          tripsLocal: values[2],
          tripsNational: values[3],
          tripsInternational: values[4],
          tripsSummary: values[5],
          poiStats: values[6],
          tripsBorough: values[7],
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  _processStats(): Promise<any> {
    if (this.selectedPOI) {
      this.showStats = true
      return this._processGridStatsByPoi().then(() => {
        const currentDelivery = this.context.vertical.delivery
        const deliveries = [currentDelivery.id]
        if (this.selectedCompareDelivery) {
          deliveries.push(this.selectedCompareDelivery.id)
        }
        return Promise.all([
          this.mobilityService.getVisitorsMaxVars(
            deliveries,
            this.compareOptions,
          ),
          this.mobilityService.getVisitorsMaxFilter(
            deliveries,
            null,
            this.compareOptions,
          ),
        ]).then((values) => {
          const [maxVars, maxFilters] = values
          let overview = {
            current: {
              filters: maxFilters[0] ? maxFilters[0].reverse() : maxFilters[0],
              target: maxVars[0],
            },
            compare: null,
          }
          if (this.compareOptions) {
            overview = {
              compare: {
                filters: maxFilters[0]
                  ? maxFilters[0].reverse()
                  : maxFilters[0],
                target: maxVars[0],
              },
              current: {
                filters: maxFilters[1]
                  ? maxFilters[1].reverse()
                  : maxFilters[1],
                target: maxVars[1],
              },
            }
          }
          this.mobilityStats = {
            ...this.mobilityStats,
            overview,
          }
        })
      })
    }
    this.showStats = false
    return Promise.resolve()
  }

  async getGeneralViz() {
    const generalLayer = this.map.getLayer(this.generalLayerId)
    const vizLoaded = generalLayer && generalLayer.viz

    const viz = vizLoaded
      ? ''
      : `
      ${this.getVizCommonVariables()}
      @max_ntrips: viewportMax($ntrips)
      @min_ntrips: viewportMin($ntrips)
      @centroid: [$lng, $lat]
      symbol: blend(
        image('/assets/img/brands/${this.brand}/map/marker.png'),
        image('/assets/img/brands/${this.brand}/map/marker-selected.png'),
        eq($id, '${this.selectedPOI || '-1'}')
      )
      width: blend(
        20,
        30,
        eq($id, '${this.selectedPOI || '-1'}')
      )
      order: desc(width())
      filter: eq($competitor, 0)
    `
    return viz
  }

  getHoverEvents(
    blendDelay = 50,
    useFeatureEnter = false,
  ): Record<string, Function> {
    return {
      featureEnter: (ev) => {
        if (useFeatureEnter) {
          this.updateHoverPopup(ev)
          ev.features.forEach((feat) => {
            feat.strokeColor.blendTo(
              `opacity(${
                this.isLightBasemap() ? Colors.colorN50 : Colors.colorN100
              }, 1)`,
              blendDelay,
            )
            feat.strokeWidth.blendTo('2', blendDelay)
          })
        }
      },
      featureHover: (ev) => {
        this.createHoverPopup(ev)
      },
      featureLeave: (ev) => {
        if (ev.features.length > 0) {
          ev.features.forEach((feat) => {
            feat.strokeColor.reset(blendDelay)
            feat.strokeWidth.reset(blendDelay)
          })
          this.hideHoverPopup()
        }
      },
    }
  }

  onPOISelected(event) {
    super.onPOISelected(event)
    this.isIsochroneActive = false
    this.onIsochroneConfigChange(null)

    this.selectedPOICentroid = event.centroid

    this.blendGeneralLayer(this.map.getLayer(this.generalLayerId)._viz)

    if (event && event.centroid) {
      this.map.flyTo({
        center: event.centroid,
      })
    }

    this.changes.detectChanges()
  }

  updateFilteredPois() {
    this.filteredPoiList = this.currentSelectValue
      ? this.poiList.filter((poi) => poi.label !== this.currentSelectValue)
      : this.poiList
  }

  async onPOICompareSelected(event) {
    super.onPOICompareSelected(event)
    await this.blendGeneralLayer(this.map.getLayer(this.generalLayerId)._viz)
    if (this.mapLoaded && this.selectedComparePOI) {
      this.flyToPoisBbox()
    }
  }

  onCompetitorsFilterChange(viz = null) {
    viz = viz || this.map.getLayer(this.generalLayerId)._viz
    const s = carto.expressions

    const competitorList = []
    for (const c of this.competitorSelectorList) {
      if (c.visible) {
        competitorList.push(c.name)
      }
    }

    let filter
    if (competitorList.length > 0) {
      filter = s.or(
        s.eq(s.prop('competitor'), 0),
        s.in(s.prop('category_name'), competitorList),
      )
    } else {
      filter = s.eq(s.prop('competitor'), 0)
    }
    viz.filter.blendTo(filter, 0).then((_) => {
      this.blendGeneralLayer(viz)
      this._processPoiList()
      this.updateFilteredPois()
    })
  }

  async blendGeneralLayer(viz) {
    const s = carto.expressions

    const selectedPOIBrand = this.selectedPOI
      ? this.allPois.find((a) => a.id === this.selectedPOI).category_name
      : this.brand
    const selectedComparePOIBrand = this.selectedComparePOI
      ? this.allPois.find((a) => a.id === this.selectedComparePOI).category_name
      : this.brand

    const visibleCompetitors = this.competitorSelectorList.filter(
      (c) => c.visible,
    )
    const competitorBrands = visibleCompetitors.map((c) => c.name)
    let imageExpression = null

    if (competitorBrands.length > 0) {
      const brands = competitorBrands.concat(this.brand)
      const images = visibleCompetitors
        .map((c) => s.image(`/assets/img/brands/${c.name}/map/marker.png`))
        .concat(s.image(`/assets/img/brands/${this.brand}/map/marker.png`))

      imageExpression = s.ramp(
        s.buckets(s.prop('category_name'), brands),
        images,
      )
    } else {
      imageExpression = s.image(
        `/assets/img/brands/${this.brand}/map/marker.png`,
      )
    }

    const markerSelectedUrl = `/assets/img/brands/${selectedPOIBrand}/map/marker-selected.png`
    const markerCompareSelectedUrl = `/assets/img/brands/${selectedComparePOIBrand}/map/marker-selected-compare.png`

    await viz.symbol.blendTo(
      s.blend(
        s.blend(
          imageExpression,
          s.image(markerCompareSelectedUrl),
          s.eq(s.prop('id'), this.selectedComparePOI || '-1'),
        ),
        s.image(markerSelectedUrl),
        s.eq(s.prop('id'), this.selectedPOI || '-1'),
      ),
      250,
    )

    await viz.width.blendTo(
      s.blend(
        20,
        30,
        s.or(
          s.eq(s.prop('id'), this.selectedPOI || '-1'),
          s.eq(s.prop('id'), this.selectedComparePOI || '-1'),
        ),
      ),
      500,
    )
  }

  onIsochroneConfigToggle() {
    this.isIsochroneActive = !this.isIsochroneActive
    this.onIsochroneConfigChange(
      this.isIsochroneActive ? this.isochroneConfigWidget.currentConfig : null,
    )
  }

  async onIsochroneConfigChange(data) {
    this.context.isoline = data && { range: data.time * 60, mode: data.mode }
    const layer = this.map.getLayer(this.isochroneLayerId)
    if (this.isIsochroneActive) {
      const isoline = await this.isolinesService.getIsoLine(
        this.selectedPOI,
        this.selectedPoiRegion,
        data.mode,
        data.time * 60,
        'time',
      )
      const values = await Promise.all([
        this.isolinesService.getIsoLineArea(
          this.selectedPOI,
          this.selectedPoiRegion,
          data.mode,
          data.time * 60,
          'time',
        ),
        this.isolinesService.getPoisWitinIsoLine(
          this.selectedPOI,
          this.selectedPoiRegion,
          data.mode,
          data.time * 60,
          'time',
        ),
        this._processMap(),
        this._processStats(),
      ])
      this.isochroneStats.area = values[0] / 1000000
      this.isochroneStats.poisTotal = values[1].total
      this.isochroneStats.poisCompetitors = values[1].competitors
      if (layer) {
        layer.show()
        this.map.updateGeoJSONSource(this.isochroneLayerId, isoline)
      } else {
        const layerConfig: ICartoLayerConfig = {
          id: this.isochroneLayerId,
          source: this.map.createGeoJSONSource(isoline),
          viz: `
            strokeWidth: 2.5
            strokeColor: opacity(${Colors.colorAccent}, 0.8)
            color: opacity(${Colors.colorAccent}, 0.1)
          `,
          before: this.generalLayerId,
        }
        this.map.addLayer(layerConfig)
        this.isLoading = false
      }
      this.map.flyToBbox(bbox(isoline), {
        padding: { top: 102, bottom: 30, left: 0, right: 240 },
      })
    } else {
      if (layer) {
        layer.hide()
      }
      this._processMap()
      this._processStats()
    }
  }

  paintPOIMap() {
    this.isLoading = true
    return Promise.all([
      this.mobilityService.getMap({
        selectedPoi: this.selectedPOI,
        compareOptions: this.compareOptions,
        highlightDst: this.highlightDst,
      }),
      this.mobilityService.getMap({ selectedPoi: null, useGeneralMap: true }),
    ])
      .then(async (values) => {
        const poiLayerData = values[0]
        const generalLayerData = values[1]
        const poiTilesURL = this.getTilesUrl(poiLayerData.metadata)

        this.map
          .updateVectorTileSource(
            this.poiLayerId,
            poiTilesURL,
            this.poiLayerProperties,
            this.getPoiViz(),
          )
          .then(() => {
            this.map.showCartoLayer(this.poiLayerId)
            this.map.moveLayer(this.poiLayerId, this.beforeLayoutPoi)
            if (this.poiPopup) {
              this.poiPopup.remove()
            }
          })

        this.map
          .updateVectorTileSource(
            this.generalLayerId,
            this.getTilesUrl(generalLayerData.metadata),
            this.generalLayerProperties,
            await this.getGeneralViz(),
            this.generalIdProperty,
          )
          .then(() => {
            this.map.moveLayer(this.generalLayerId, this.beforeLayoutGeneral)
          })

        this.isLoading = false
        this.mapLoaded = true
        this.changes.markForCheck()

        if (this.threeDMarker && this.threeDmode) {
          this.threeDMarker.getElement().classList.add('active')
        }

        const contextCity = this.context.getContext().city.id
        if (contextCity !== this.currentCity) {
          this.currentCity = contextCity
          this.flyToBbox()
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  flyToPoisBbox() {
    const poi = this.allPois.find((p) => p.id === this.selectedPOI)
    const comparedPOI = this.allPois.find(
      (p) => p.id === this.selectedComparePOI,
    )
    const minX = Math.min(poi.lng, comparedPOI.lng)
    const maxX = Math.max(poi.lng, comparedPOI.lng)
    const minY = Math.min(poi.lat, comparedPOI.lat)
    const maxY = Math.max(poi.lat, comparedPOI.lat)

    const bbox = [minX, minY, maxX, maxY]

    this.map.flyToBbox(bbox, {
      padding: 300,
      speed: 0.5,
    })
  }
}
